import type { Session, User } from '@supabase/supabase-js';
import { isAccessTokenExpired } from '../utils/jwt';

/**
 * In-memory auth snapshot.
 *
 * Goal:
 * - Avoid calling supabase.auth.getSession()/getUser() from random services (can hang on corrupted storage).
 * - Keep a single source of truth updated by AuthContext (and potentially by other top-level boot logic).
 */
type AuthSnapshot = {
  session: Session | null;
  user: User | null;
  updatedAt: number;
};

export type SessionSyncResult =
  | { status: 'ok'; token: string }
  | { status: 'unchanged'; token: string | null }
  | { status: 'logged_out' };

export const AUTH_SESSION_LOST_EVENT = 'leadsme:auth-session-lost';

/** Thrown when a logged-in user no longer has a usable access token (do not fall back to anon RLS). */
export class AuthSessionLostError extends Error {
  constructor() {
    super('AUTH_SESSION_LOST');
    this.name = 'AuthSessionLostError';
  }
}

export function isAuthSessionLostError(err: unknown): boolean {
  return (
    err instanceof AuthSessionLostError ||
    (err instanceof Error && (err.name === 'AuthSessionLostError' || err.message === 'AUTH_SESSION_LOST'))
  );
}

/** GoTrue can wait forever on navigator locks (-1). Cap refresh so DB calls never hang after idle. */
const REFRESH_TIMEOUT_MS = 8000;

/** Avoid flooding AuthContext when several requests fail at once. */
const SESSION_LOST_DEBOUNCE_MS = 1500;

/**
 * After forceLoggedOut, in-flight supabaseData calls may still run without a Bearer.
 * Keep a short window so fetch can fail closed (401) instead of silent empty RLS.
 */
let sessionLostGuardUntil = 0;

let snapshot: AuthSnapshot = {
  session: null,
  user: null,
  updatedAt: Date.now(),
};

let refreshPromise: Promise<SessionSyncResult> | null = null;
let lastSessionLostAt = 0;

function notifySessionLost(): void {
  if (typeof window === 'undefined') return;
  const now = Date.now();
  if (now - lastSessionLostAt < SESSION_LOST_DEBOUNCE_MS) return;
  lastSessionLostAt = now;
  window.dispatchEvent(new CustomEvent(AUTH_SESSION_LOST_EVENT));
}

function forceLoggedOut(): SessionSyncResult {
  // Cover in-flight requests that already got a null token from accessToken().
  sessionLostGuardUntil = Date.now() + 15_000;
  clearAuthSnapshot();
  notifySessionLost();
  return { status: 'logged_out' };
}

function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const id = setTimeout(() => reject(new Error(label)), ms);
    promise.then(
      (value) => {
        clearTimeout(id);
        resolve(value);
      },
      (err) => {
        clearTimeout(id);
        reject(err);
      }
    );
  });
}

export function setAuthSnapshot(next: { session: Session | null; user: User | null }): void {
  snapshot = {
    session: next.session,
    user: next.user,
    updatedAt: Date.now(),
  };
  if (next.user) {
    sessionLostGuardUntil = 0;
  }
}

export function clearAuthSnapshot(): void {
  snapshot = {
    session: null,
    user: null,
    updatedAt: Date.now(),
  };
}

export function getAuthSnapshot(): AuthSnapshot {
  return snapshot;
}

export function getAccessToken(): string | null {
  return snapshot.session?.access_token ?? null;
}

export function getUserId(): string | null {
  return snapshot.user?.id ?? null;
}

/** True briefly after session loss — data fetch must not succeed as anon. */
export function isSessionLostGuardActive(): boolean {
  return sessionLostGuardUntil > Date.now();
}

/**
 * Called when PostgREST rejects the JWT (expired / invalid).
 * Triggers the same logout path as a failed refresh.
 */
export function reportUnauthorizedApiResponse(): void {
  if (!snapshot.user && !isSessionLostGuardActive()) return;
  forceLoggedOut();
}

async function refreshSessionToken(): Promise<SessionSyncResult> {
  const { supabaseAuth } = await import('./supabase');
  const { isInvalidRefreshTokenError } = await import('../utils/authReset');

  if (!snapshot.user) {
    return { status: 'unchanged', token: getAccessToken() };
  }

  try {
    const { data: { session }, error } = await withTimeout(
      supabaseAuth.auth.refreshSession(),
      REFRESH_TIMEOUT_MS,
      'REFRESH_TIMEOUT'
    );

    if (error) {
      if (isInvalidRefreshTokenError(error)) {
        return forceLoggedOut();
      }
      // Soft network/API blip: keep working only while the access token is still usable.
      const token = getAccessToken();
      if (!token || isAccessTokenExpired(token)) {
        return forceLoggedOut();
      }
      return { status: 'unchanged', token };
    }

    if (session?.access_token && session.user) {
      setAuthSnapshot({ session, user: session.user });
      return { status: 'ok', token: session.access_token };
    }

    return forceLoggedOut();
  } catch {
    const token = getAccessToken();
    // Hung or failed refresh with an already-expired JWT → clear zombie UI.
    if (!token || isAccessTokenExpired(token)) {
      return forceLoggedOut();
    }
    return { status: 'unchanged', token };
  }
}

/**
 * Ensure the in-memory access token is still valid.
 * Refreshes via supabaseAuth when expired (standard SaaS resume pattern).
 */
export async function syncAccessToken(): Promise<SessionSyncResult> {
  const token = getAccessToken();
  if (token && !isAccessTokenExpired(token)) {
    return { status: 'unchanged', token };
  }

  if (!snapshot.user) {
    return { status: 'unchanged', token };
  }

  if (!refreshPromise) {
    refreshPromise = refreshSessionToken().finally(() => {
      refreshPromise = null;
    });
  }

  return refreshPromise;
}

/**
 * Used by supabaseData accessToken callback before each request.
 * If the user was logged in and the session is dead, throws AuthSessionLostError
 * so we never hit PostgREST as anon (empty RLS lists with a still-"logged in" UI).
 */
export async function ensureFreshAccessToken(): Promise<string | null> {
  const hadUser = Boolean(snapshot.user);
  const result = await syncAccessToken();

  if (result.status === 'logged_out') {
    throw new AuthSessionLostError();
  }

  const token = result.token;
  if (token && !isAccessTokenExpired(token)) {
    return token;
  }

  if (hadUser || isSessionLostGuardActive()) {
    forceLoggedOut();
    throw new AuthSessionLostError();
  }

  return null;
}
